"use client";

import Image from "next/image";
import CheckIcon from "@/icons/status/check-circle.svg";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Check from "@/icons/status/check-circle-v2.svg";
import { QuizCTA } from "~/components/features/quiz";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { cn } from "~/lib/utils";
import Reveal from "~/componentsDS/Reveal";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import ElementorContainer from "~/componentsDS/ElementorLoyout/ElementorContainer";
import useLpCtaText from "~/hooks/useLpCtaText";
import StarRating from "~/componentsDS/StarRating";

const featuresData = [
    {
        title: <>Compounded in a <br />Licensed U.S. Pharmacy</>,
        description: "503A U.S. pharmacy. NABP, LegitScript & PCAB certified.",
        mobileTitle: "Made in U.S.A",
    },
    {
        title: "Clinician-Approved Prescription Treatment",
        description: "HSA/FSA eligible. Secure.",
        mobileTitle: "Clinician-Approved Prescription",
    },
    {
        title: "Bioidentical Estradiol + Micronized Progesterone",
        description: "Clinically proven & third-party tested.",
        mobileTitle: "Bioidentical Estradiol + Micronized Progesterone",
    },
];

const heroInfoData = [
    <>One cream replaces your entire <br className="hidden max-[389px]:block" />HRT regimen</>,
    <>Clinically-backed estradiol <br className="hidden max-[389px]:block" />+ progesterone</>,
    <>100% online, personalized and <br className="hidden max-[389px]:block" />supported care</>,
];

const HeroInfo = () => {
    const ctaTitle = useLpCtaText("Take the Quiz");
    return (
        <div className="flex flex-col w-full md:min-w-0 md:flex-1">
            <StarRating className="mb-4 md:mb-6 max-[389px]:scale-[0.95] max-[389px]:origin-left text-pureWhite" />

            <Heading
                as="h1"
                size="sm"
                sizes={{ md: "md" }}
                className="mb-4"
            >
            Feel like yourself again — <br />
            in one <Heading as="i" font="featureDisplay" weight="400" className="text-[28px] md:text-[36px]">simple</Heading> cream
            </Heading>

            <Paragraph size="lg" sizes={{ md: "xl" }} weight="500" className="mb-2 max-[389px]:text-[16px]">
            Whole-body hormone therapy, made for you:
            </Paragraph>
            <ul className="flex flex-col gap-2 max-md:-mr-1">
                {heroInfoData.map((item, index) => (
                    <li key={index} className="flex items-start gap-1 md:gap-2">
                        <CheckIcon className="w-[28px] h-[28px] shrink-0" />
                        <Paragraph size="md" sizes={{ md: "xl" }} className="max-[389px]:pt-[2px] pt-[5px]">{item}</Paragraph>
                    </li>
                ))}
            </ul>

            <div className="w-full md:w-fit mt-6 md:mt-10">
                <QuizCTA
                    className="w-full px-8 md:w-auto"
                    href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                    eventData={{
                        event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                        widget: "lp_main",
                        meta_data: {
                            click_text: "Take the Quiz",
                        },
                    }}
                >
                    {ctaTitle}
                </QuizCTA>

                <div className="mt-2.5 md:mt-4 flex w-full items-center justify-center gap-2">
                    <Check width={21} height={19} className="text-pureWhite" />
                    <Paragraph size="sm" weight="500" className="pt-[2px]">
                    HSA/FSA eligible
                    </Paragraph>
                </div>
            </div>
        </div>
    );
};

const HeroSection = () => {
    return (
        <section>
            <div className="flex relative overflow-hidden bg-[#DCE6F5] rounded-b-[24px] md:min-h-[100vh] font-suisseBP text-primary500 min-h-[100dvh]">
                <div className="absolute inset-0 md:h-[1050px] lg:h-[1200px]">
                    <Image
                        src="https://res.cloudinary.com/dirscuuhp/images/v1776425973/oestra_5070857d1f/oestra_5070857d1f.webp"
                        alt="Smiling woman experiencing relief from hormone therapy symptoms"
                        fill
                        priority
                        quality={95}
                        className="hidden md:block object-cover object-[70%_50%] lg:object-[100%_10%]"
                        sizes="100vw"
                    />
                    <Image
                        src="https://res.cloudinary.com/dirscuuhp/image/upload/v1780016199/new_design/Frame_abisqc.webp"
                        alt="Smiling woman experiencing relief from hormone therapy symptoms"
                        fill
                        priority
                        className="md:hidden object-cover object-[50%_48px]"
                        sizes="100vw"
                    />
                </div>

                <div className="
                    absolute inset-0
                    bg-[linear-gradient(180deg,_#B5C5DE_0%,_#B5C5DE_5.25%,_transparent_8.27%),linear-gradient(180deg,_transparent_39%,_rgba(16,19,26,0.58)_52%,_rgba(16,19,26,0.73)_82%,_rgba(16,19,26,0.89)_94%)]
                    md:bg-[linear-gradient(82deg,_rgba(16,19,26,0.65)_1.44%,_rgba(16,19,26,0.29)_30.7%,_rgba(16,19,26,0)_53.72%)]
                "/>

                <ElementorContainer
                    className="lg:absolute lg:inset-0 z-10 pt-[230px] md:pt-[160px] lg:pt-0 max-lg:pb-14"
                    innerClassName="flex h-full md:items-end lg:pb-24"
                >
                    <div className="flex w-full flex-col lg:flex-row lg:items-end lg:justify-between lg:gap-8 text-pureWhite max-md:mt-auto">
                        <Reveal>
                            <HeroInfo />
                        </Reveal>

                        <Reveal className="md:h-full lg:w-1/2 md:mt-8 lg:mt-0 flex flex-col max-md:hidden">
                            <div className="mt-4 md:mt-8 lg:mt-0 flex flex-wrap justify-center gap-1 md:gap-2 md:grid md:grid-cols-3">
                                {featuresData.map((item, index) => (
                                    <div
                                        key={index}
                                        className={cn(
                                            "rounded-[12px] max-[399px]:py-2 py-3 px-2 md:px-3 text-pureWhite",
                                            "bg-trueBlack/25 backdrop-blur-[50px]",
                                            index === 2 ? "w-full text-center" : "grow basis-auto",
                                            "md:w-auto md:grow-0 md:basis-auto md:min-h-[132px] md:rounded-[14px] md:border md:border-white/5 md:p-4 md:text-left",
                                        )}
                                    >
                                        <p className="max-[399px]:text-[14px] text-[16px] text-center font-medium leading-[1.25] md:hidden">
                                            {item.mobileTitle}
                                        </p>

                                        <div className="hidden md:block">
                                            <p className="text-[18px] font-medium leading-[1.3]">
                                                {item.title}
                                            </p>
                                            <p className="mt-4 text-[14px] leading-[1.3] text-pureWhite/70">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </ElementorContainer>
            </div>
        </section>
    );
}

export default HeroSection;
